import { nameImportedFeatures } from './map-feature-names';
import * as turf from '@turf/turf';
import type * as mapboxgl from 'mapbox-gl';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { ChangeDetectorRef } from '@angular/core';
import type { FeatureCollection, Geometry, GeoJsonProperties } from 'geojson';
import { firstValueFrom, fromEvent, takeUntil } from 'rxjs';
import type { GeoParseResponse, GeoUploadService } from '../../../common/service/geo-upload.service';
import type { TranslationService } from '../../../common/service/translation-service.service';
import type { DrawValidationService } from './services/draw-validation.service';
import { EMPTY_FC, ADD_CHUNK_SIZE, SIZE_SOFT_LIMIT, TOLERANCE_STEPS, HIGH_QUALITY } from './map-editor.constants';
import { findInvalidCoord, normalizeForDraw, orientedFeature } from './map-geometry.utils';
import { raf } from './map-editor-scheduling';

export interface MapGeoJsonIOContext {
  draw: MapboxDraw;
  map: mapboxgl.Map;
  cdr: Pick<ChangeDetectorRef, 'markForCheck'>;
  geoUpload: GeoUploadService;
  translation: TranslationService;
  drawValidation: DrawValidationService;
  isUploading: boolean;
  uploadProgress: number;
  uploadSignal?: AbortSignal;
  loadingProgress?: (completed: number, total: number) => void;
  hintText: string | null;
  hasUnsavedChanges: boolean;
  showMsg: (key: string) => void;
  showErr: (key: string, error?: unknown) => void;
  convertPointFeatures: (input: any) => any;
  enforceLeftHandRuleAll: () => void;
  handleDrawChange: () => void;
  validateAll: (notify?: boolean) => Promise<boolean>;
  updateErrorHighlights: () => void;
  refreshCtrl: () => void;
  recordHistory: () => void;
}

export interface GeoJsonImportResult {
  loaded: number;
  total: number;
  skipped: number;
  skippedTypes: string[];
}

const IMPORTABLE_GEOMETRY_TYPES = new Set(['Polygon', 'MultiPolygon', 'Point', 'MultiPoint']);
const FAILED_ADD_TYPE = 'invalid';
const MISSING_GEOMETRY_TYPE = 'none';

export function downloadAllPolygonsAsGeoJSON(this: MapGeoJsonIOContext): void {
  if (!this.draw) return;
  try {
    const all = (this.draw.getAll() as FeatureCollection<Geometry, GeoJsonProperties>) ?? EMPTY_FC;
    const features = (all.features || []).filter(
      (f) => f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'Point'
    );
    if (!features.length) return this.showMsg('No_polygons_found_to_export_tr');
    const fc: FeatureCollection = { type: 'FeatureCollection', features } as any;
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/geo+json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `polygons-${ts}.geojson`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
    this.showMsg('GeoJSON_downloaded_tr');
  } catch (err) {
    this.showErr('Could_not_export_polygons_tr', err);
  }
}

export async function handleUpload(this: MapGeoJsonIOContext, files: FileList) {
  const file = files?.[0];
  if (!file) return;
  const name = (file.name || '').toLowerCase();
  const allowed = /\.geojson$|\.json$|\.zip$|\.csv$|\.xls$|\.xlsx$|\.qgz$|\.gpx$/.test(name);
  if (!allowed) return this.showMsg('Unsupported_map_files_tr');

  try {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.hintText = 'Uploading… 0%';
    this.cdr.markForCheck();
    const request = this.geoUpload.parseFileToGeoJSON(file, (pct) => {
      const progress = Math.max(0, Math.min(100, Math.round(pct)));
      this.uploadProgress = progress;
      this.hintText = `Uploading… ${progress}%`;
      this.cdr.markForCheck();
    });
    this.uploadSignal?.throwIfAborted();
    const resp: GeoParseResponse = await firstValueFrom(
      this.uploadSignal ? request.pipe(takeUntil(fromEvent(this.uploadSignal, 'abort'))) : request
    );
    this.uploadSignal?.throwIfAborted();
    if (resp?.success === false || resp?.error) {
      return this.showErr(resp.error ?? 'Server_returned_an_error_processing_the_file_tr');
    }
    if (!resp?.geojson) return this.showErr('Server_did_not_return_parsed_GeoJSON_tr');

    const bad = findInvalidCoord(resp.geojson);
    if (bad) {
      this.showErr(
        `${await this.translation.loadFromBackend('Invalid_coordinates_in_file_Offender_at_tr')} '${bad.path}: lat=${bad.lat}, lng=${bad.lng}'`
      );
      return;
    }

    const existingFeatureIds = new Set(this.draw.getAll().features.map((feature) => feature.id));
    const result = await addGeoJsonToDraw.call(this, withoutFeatureIds(resp.geojson), this.uploadSignal);
    if (!result) return;
    if (result.loaded === 0) {
      if (result.skippedTypes.includes(FAILED_ADD_TYPE)) await reportImportResult.call(this, result);
      return;
    }
    this.enforceLeftHandRuleAll();
    this.handleDrawChange();
    this.validateAll(result.skipped === 0);
    focusImportedFeatures.call(this, existingFeatureIds);
    this.hasUnsavedChanges = true;
    this.updateErrorHighlights();
    this.recordHistory();
    await reportImportResult.call(this, result);
    if (!this.uploadSignal?.aborted) this.map.once('idle', () => this.drawValidation?.recompute());
  } catch (err) {
    if (!this.uploadSignal?.aborted) this.showErr('Upload_or_server_parsing_failed_tr', err);
  } finally {
    this.isUploading = false;
    this.uploadProgress = 0;
    this.hintText = '';
    this.cdr.markForCheck();
  }
}

function focusImportedFeatures(this: MapGeoJsonIOContext, existingFeatureIds: Set<string | number | undefined>): void {
  const importedFeatures = this.draw
    .getAll()
    .features.filter((feature) => !existingFeatureIds.has(feature.id));
  if (!importedFeatures.length) return;

  const [west, south, east, north] = turf.bbox({ type: 'FeatureCollection', features: importedFeatures } as any);
  if (![west, south, east, north].every(Number.isFinite)) return;

  this.map.flyTo({ center: [(west + east) / 2, (south + north) / 2], zoom: 14 });
}

async function reportImportResult(this: MapGeoJsonIOContext, result: GeoJsonImportResult) {
  if (result.skipped === 0) return this.showMsg('File_parsed_on_server_and_loaded_for_editing_tr');
  const message = await this.translation.loadFromBackend('Map_import_partial_tr', {
    loaded: result.loaded,
    total: result.total,
    types: result.skippedTypes.join(', '),
  });
  if (this.uploadSignal?.aborted) return;
  this.showErr(message);
}

function withoutFeatureIds(geojson: any): any {
  const strip = (feature: any) => {
    if (!feature || typeof feature !== 'object' || !('id' in feature)) return feature;
    const { id: _id, ...rest } = feature;
    return rest;
  };
  if (geojson?.type === 'FeatureCollection') return { ...geojson, features: (geojson.features ?? []).map(strip) };
  if (geojson?.type === 'Feature') return strip(geojson);
  return geojson;
}

function featuresOf(prepared: any): any[] {
  if (prepared?.type === 'FeatureCollection') return prepared.features ?? [];
  return prepared?.type === 'Feature' ? [prepared] : [];
}

function droppedGeometryTypes(features: any[]): string[] {
  return features
    .filter((feature) => !feature?.geometry || !IMPORTABLE_GEOMETRY_TYPES.has(feature.geometry.type))
    .map((feature) => feature?.geometry?.type ?? MISSING_GEOMETRY_TYPE);
}

function addOneByOne(draw: MapboxDraw, batch: any[]): number {
  let failed = 0;
  for (const feature of batch) {
    try {
      (draw as any).add(feature);
    } catch {
      failed++;
    }
  }
  return failed;
}

function addBatch(draw: MapboxDraw, batch: any[]): number {
  try {
    (draw as any).add({ type: 'FeatureCollection', features: batch });
    return 0;
  } catch {
    return addOneByOne(draw, batch);
  }
}

function importResult(importable: number, failed: number, dropped: string[]): GeoJsonImportResult {
  const skippedTypes = [...new Set(dropped)];
  if (failed > 0) skippedTypes.push(FAILED_ADD_TYPE);
  return {
    loaded: importable - failed,
    total: importable + dropped.length,
    skipped: dropped.length + failed,
    skippedTypes,
  };
}

export async function addGeoJsonToDraw(
  this: MapGeoJsonIOContext,
  input: any,
  signal?: AbortSignal
): Promise<GeoJsonImportResult | null> {
  const before = signal ? this.draw.getAll() : null;
  try {
    signal?.throwIfAborted();
    const prepared = this.convertPointFeatures(input);
    const feats = normalizeForDraw(prepared);
    const preparedFeatures = featuresOf(prepared);
    const points = preparedFeatures.filter((feature: any) => feature?.geometry?.type === 'Point');
    const dropped = droppedGeometryTypes(preparedFeatures);
    if (!feats.length && !points.length) {
      this.showMsg('No_polygon_features_found_in_the_file_tr');
      return importResult(0, 0, dropped);
    }

    const orientedFeats = nameImportedFeatures([...feats.map(orientedFeature), ...points], this.draw.getAll().features);
    let failed = 0;
    if (orientedFeats.length <= ADD_CHUNK_SIZE) {
      failed = addBatch(this.draw, orientedFeats);
    } else {
      for (let i = 0; i < orientedFeats.length; i += ADD_CHUNK_SIZE) {
        signal?.throwIfAborted();
        failed += addBatch(this.draw, orientedFeats.slice(i, i + ADD_CHUNK_SIZE));
        this.loadingProgress?.(Math.min(i + ADD_CHUNK_SIZE, orientedFeats.length), orientedFeats.length);
        await raf();
      }
    }
    signal?.throwIfAborted();
    this.loadingProgress?.(orientedFeats.length, orientedFeats.length);
    this.handleDrawChange();
    this.refreshCtrl();
    this.cdr.markForCheck();
    this.hasUnsavedChanges = true;
    this.updateErrorHighlights();
    return importResult(orientedFeats.length, failed, dropped);
  } catch (err) {
    if (signal?.aborted) {
      if (before) this.draw.set(before);
      this.handleDrawChange();
      this.refreshCtrl();
      throw err;
    }
    this.showErr('Invalid_GeoJSON_Could_not_display_on_map_tr', err);
    return null;
  }
}

export function countPositions(g: any): number {
  let n = 0;
  const walk = (coords: any) => {
    if (!coords) return;
    if (typeof coords[0] === 'number') {
      n += 1;
      return;
    }
    for (const c of coords) walk(c);
  };
  const each = (f: any) => f?.geometry && walk(f.geometry.coordinates);
  if (g?.type === 'FeatureCollection') g.features?.forEach(each);
  else if (g?.type === 'Feature') each(g);
  else if (g?.type) walk(g.coordinates);
  return n;
}

export function trySimplifyIfLarge(fc: FeatureCollection) {
  const jsonLen = JSON.stringify(fc).length;
  const beforePts = countPositions(fc);
  if (jsonLen <= SIZE_SOFT_LIMIT) {
    return {
      result: fc,
      simplified: false,
      beforePts,
      afterPts: beforePts,
      tolUsed: undefined as number | undefined,
    };
  }
  let current: any = fc;
  let used: number | undefined;
  for (const tol of TOLERANCE_STEPS) {
    try {
      const simplified = turf.simplify(current as any, { tolerance: tol, highQuality: HIGH_QUALITY });
      const len = JSON.stringify(simplified).length;
      used = tol;
      current = simplified;
      if (len <= SIZE_SOFT_LIMIT) break;
    } catch {}
  }
  const afterPts = countPositions(current);
  return { result: current as FeatureCollection, simplified: true, beforePts, afterPts, tolUsed: used };
}
